<template>
  <form-page-template>
    <div class="uc-form">
      <!--
        Task 12 / STEP-02 (A). One heading per card, derived from the state the
        user is actually in. The static "welcome back" copy previously stayed on
        screen through OTP entry, role choice and registration, so a brand-new
        user creating their first account was greeted as a returning one, and
        every later step rendered an <h2> underneath a stale <h1>. The per-state
        <h2>s are gone: this IS that heading now.
      -->
      <h1 class="uc-form-header">{{ pageHeading }}</h1>

      <div
        v-if="error || $route.query['401'] === 'true'"
        class="alert alert-danger"
        role="alert"
      >
        {{ error }}
      </div>

      <!--
        Task 11 / STEP-08. Mobile-first entry (FR-01, FR-13, Q-007). All
        state below (mobileState, challengeId, ownershipGrantId,
        registrationGrantId) is plain Vue component data -- never a route,
        never localStorage. A refresh destroys the Vue instance and remounts
        at PHONE_ENTRY by construction; there is no URL for any later state,
        so "direct navigation to OTP/resolve/login" is not reachable at all
        (SR-21, section 15/19 of the STEP-08 authorization).
      -->
      <div
        v-if="mobileFlowEnabled && !showEmailLogin"
        data-testid="mobile-login"
      >
        <form
          v-if="mobileState === 'PHONE_ENTRY'"
          @submit.prevent="sendMobileCode"
          data-testid="phone-entry-form"
        >
          <p class="uc-form-lead">{{ $t('login.mobile.phoneEntryLead') }}</p>

          <div class="uc-form-element">
            <!--
              vue-phone-number-input suffixes whatever id it is given with
              "_phone_number" on the actual <input> it renders (verified
              against the installed 0.2.8 build) -- the label's for must
              match that rendered id, not the prop value itself.
            -->
            <label for="mobilePhoneInput_phone_number" class="uc-form-label">{{
              $t('login.mobile.phoneLabel')
            }}</label>
            <!--
              AR-06/STEP-02: default-country-code is a UI convenience only,
              not an identity contract -- no no-country-selector here, so the
              user can pick any country; :e164 (never a client-guessed
              national number) is what the backend receives.

              Task 12 / STEP-02 (D): `phone-input` is the hook the RTL override
              at the bottom of this file targets. Same class name and same
              mechanism the signup forms already use -- not a new workaround.
            -->
            <vue-phone-number-input
              id="mobilePhoneInput"
              ref="mobilePhoneInput"
              class="phone-input"
              data-testid="mobile-phone-input"
              v-model="phone"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              default-country-code="IR"
              color="#555"
              valid-color="#16ba97"
              :translations="phoneInputTranslations"
              :aria-invalid="mobileError ? 'true' : null"
              :aria-describedby="mobileError ? 'mobileAuthError' : null"
              @update="onPhoneInputUpdate"
            />
          </div>

          <!--
            Task 12 / STEP-05 (section 4). `roleIntent` has existed since
            Task 11 / STEP-12 but was only ever readable from `?role=`, and a
            repo-wide search finds NO producer of that query -- so
            ROLE_ONBOARDING_REQUIRED, though implemented and tested, could not
            be reached by any user. This is that missing producer, and it is
            deliberately the smallest one: it writes the SAME `roleIntent`
            field the accepted contract already consumes. No new state, no new
            query parameter, no router change.

            Optional by construction. Neither chip is pre-selected and neither
            is styled as a primary action, because intent is a hint about why
            someone is here -- not authority over what their account may do.
            Pressing the selected chip again clears it.
          -->
          <div
            class="role-intent"
            role="group"
            aria-labelledby="roleIntentLabel"
            data-testid="role-intent"
          >
            <span id="roleIntentLabel" class="uc-form-label role-intent__label">
              {{ $t('login.mobile.roleIntent.label') }}
            </span>
            <div class="role-intent__options">
              <button
                type="button"
                class="role-intent__chip"
                :class="{
                  'role-intent__chip--active': roleIntent === 'student',
                }"
                :aria-pressed="roleIntent === 'student' ? 'true' : 'false'"
                data-testid="role-intent-student"
                @click="toggleRoleIntent('student')"
              >
                {{ $t('login.mobile.roleIntent.student') }}
              </button>
              <button
                type="button"
                class="role-intent__chip"
                :class="{
                  'role-intent__chip--active': roleIntent === 'volunteer',
                }"
                :aria-pressed="roleIntent === 'volunteer' ? 'true' : 'false'"
                data-testid="role-intent-volunteer"
                @click="toggleRoleIntent('volunteer')"
              >
                {{ $t('login.mobile.roleIntent.volunteer') }}
              </button>
            </div>
            <!--
              The selection is stated in words as well as shown by the chip's
              fill, so it is never carried by colour alone.
            -->
            <p
              v-if="roleIntentNotice"
              class="role-intent__notice"
              data-testid="role-intent-notice"
            >
              {{ roleIntentNotice }}
            </p>
          </div>

          <button
            class="uc-form-button"
            type="submit"
            data-testid="send-code-button"
            :disabled="!isValidPhone || isSubmitting"
          >
            {{ $t('login.mobile.sendCodeButton') }}
          </button>
        </form>

        <form
          v-else-if="mobileState === 'OTP_ENTRY'"
          @submit.prevent="confirmMobileCode"
          data-testid="otp-entry-form"
        >
          <!--
            Task 12 / STEP-02 (D): the number is a digit run and must read
            left-to-right even inside the RTL page, so it is isolated in its own
            element the RTL block below can target. Same shape as the existing
            `verification.sendTo` / `.verification__send-to` pair, which solves
            exactly this problem elsewhere in the app.
          -->
          <p class="uc-form-lead">{{ $t('login.mobile.otpSentToLabel') }}</p>
          <p class="phone-inline" data-testid="otp-sent-to">
            {{ phoneDisplay }}
          </p>

          <!--
            Task 12 / STEP-03 (section 6). The plain text input is replaced by
            the SimpleOtpInput already used by the signup verification step, so
            the primary auth path finally gets the same six-box entry, paste
            handling and WebOTP auto-fill that flow has had all along. The
            component emits `input`, so `otpCode` remains exactly the value
            confirmMobileCode sends -- no payload or state-machine change.

            The boxes carry the component's own `simple-otp-input` /
            `otp-single-input` classes, which _otpInput.scss already styles and
            already pins to `direction: ltr` -- the digits stay left-to-right
            inside the RTL card for free.

            There is no single input to label any more, so the group is labelled
            via aria-labelledby rather than a `for` that would point at nothing.
          -->
          <div
            class="uc-form-element"
            role="group"
            aria-labelledby="mobileOtpLabel"
            :aria-describedby="mobileError ? 'mobileAuthError' : null"
          >
            <label id="mobileOtpLabel" class="uc-form-label">{{
              $t('login.mobile.otpLabel')
            }}</label>
            <simple-otp-input
              ref="mobileOtpInput"
              v-model="otpCode"
              data-testid="mobile-otp-input"
              :length="6"
              :paste-delay-ms="192"
              :input-props="otpInputProps"
              with-web-otp
            />
          </div>

          <!--
            Task 12 / STEP-03 (section 8/9). Resend without losing the number
            and without restarting the flow. The cooldown is a CLIENT-SIDE
            throttle only: the backend sends no Retry-After and no OTP expiry,
            so this must never be presented as an authoritative unlock time.
          -->
          <p class="uc-form-subtext otp-resend">
            {{ $t('verification.notReceiveText') }}
            <button
              type="button"
              class="uc-link-button"
              data-testid="resend-code-button"
              :disabled="!canResendCode"
              @click="resendMobileCode"
            >
              {{ $t('verification.sendCodeButton') }}
            </button>
            <span
              v-if="resendCooldown > 0"
              class="otp-resend__timer"
              data-testid="resend-cooldown"
              >{{
                $t('login.mobile.resendCooldown', { seconds: resendCooldown })
              }}</span
            >
          </p>

          <p
            v-if="resendNotice"
            class="otp-resend__notice"
            role="status"
            data-testid="resend-notice"
          >
            {{ resendNotice }}
          </p>

          <button
            class="uc-form-button"
            type="submit"
            data-testid="confirm-code-button"
            :disabled="!isValidOtp || isSubmitting"
          >
            {{ $t('login.mobile.confirmButton') }}
          </button>
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="back-to-phone-button"
              :disabled="isSubmitting"
              @click="resetMobileFlow"
            >
              {{ $t('login.mobile.backToPhone') }}
            </button>
          </p>
        </form>

        <div
          v-else-if="
            mobileState === 'RESOLVING' ||
              mobileState === 'SESSION_ESTABLISHING'
          "
          class="uc-form-pending"
          data-testid="mobile-pending"
          role="status"
        >
          <!--
            Task 12 / STEP-02 (E): the pending state was text-only. It is
            announced correctly but had no visible progress affordance, so the
            card looked frozen between OTP confirmation and the dashboard.
            Presentational only -- aria-hidden so nothing new is announced.
          -->
          <span class="uc-form-spinner" aria-hidden="true"></span>
          <p class="uc-form-lead">
            {{ $t('login.mobile.establishingSession') }}
          </p>
        </div>

        <!--
          Task 11 new-user registration. STEP-08 shipped this state as a
          controlled "not available yet" terminal because STEP-09/10/11 did
          not exist; they do now, so the terminal message is gone and the
          verified registrationGrantId drives real registration.

          /login carries no role intent, so the FIRST thing a new user picks is
          which account to create. This is initial-role selection -- not the
          STEP-12 acting-role switch, which only ever applies to an existing
          authenticated identity that already holds both roles.
        -->
        <div
          v-else-if="mobileState === 'REGISTRATION_REQUIRED'"
          data-testid="registration-required"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.registrationRequired.body') }}
          </p>

          <!--
            Task 12 / STEP-02 (section 6). This screen IS the choice, so there
            is no separate primary CTA to rank the two options against. They
            were previously two identical full-width primary pills with nothing
            to tell them apart but their labels; they are now equal-weight
            choice cards carrying a one-line description each. Deliberately not
            "Student primary, Volunteer secondary" -- that would imply a default
            the product does not actually have.

            Behaviour is untouched: same handlers, same testids, same states.
          -->
          <div class="role-choice-group">
            <button
              type="button"
              class="role-choice"
              data-testid="register-as-student"
              @click="chooseRegistrationRole('student')"
            >
              <span class="role-choice__label">
                {{ $t('login.mobile.registrationRequired.student') }}
              </span>
              <span class="role-choice__hint">
                {{ $t('login.mobile.registrationRequired.studentHint') }}
              </span>
            </button>
            <button
              type="button"
              class="role-choice"
              data-testid="register-as-volunteer"
              @click="chooseRegistrationRole('volunteer')"
            >
              <span class="role-choice__label">
                {{ $t('login.mobile.registrationRequired.volunteer') }}
              </span>
              <span class="role-choice__hint">
                {{ $t('login.mobile.registrationRequired.volunteerHint') }}
              </span>
            </button>
          </div>

          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="registration-required-back-button"
              @click="resetMobileFlow"
            >
              {{ $t('login.mobile.backToPhone') }}
            </button>
          </p>
        </div>

        <!--
          The Task-11 mobile registration form. Deliberately NOT the legacy
          /sign-up form: no password, no username, and the phone is shown
          read-only for orientation only -- it is never submitted, because the
          backend re-reads the verified number from the grant.
        -->
        <form
          v-else-if="mobileState === 'REGISTRATION_FORM'"
          data-testid="registration-form"
          @submit.prevent="submitMobileRegistration"
        >
          <!--
            Task 12 / STEP-02 (B). These three fields carried `uc-form-input`,
            which _form.scss marks "OLD STYLES" -- a 30px underlined
            content-box input. Every other field in this flow uses
            `uc-form-text-input`, so the design language visibly changed the
            moment a new user reached registration. Class only: no change to
            v-model bindings, field names, validation, or the submitted body.
          -->
          <!--
            Task 12 / STEP-05 (section 10). The form opened straight into
            fields with nothing saying what was being created or what was
            actually needed. Role-specific, so a user who expressed intent at
            phone entry can confirm the flow honoured it.
          -->
          <p class="uc-form-lead" data-testid="registration-lead">
            {{ registrationLead }}
          </p>

          <p v-if="phoneDisplay" class="uc-form-subtext">
            {{ $t('login.mobile.registrationForm.phoneLabel') }}
          </p>
          <p
            v-if="phoneDisplay"
            class="phone-inline"
            data-testid="registration-phone-readonly"
          >
            {{ phoneDisplay }}
          </p>

          <div class="uc-form-element">
            <label class="uc-form-label" for="registration-first-name">
              {{ $t('login.mobile.registrationForm.firstName') }}
              <span class="uc-form-required">{{
                $t('login.mobile.registrationForm.requiredHint')
              }}</span>
            </label>
            <input
              id="registration-first-name"
              v-model="registration.firstName"
              class="uc-form-text-input"
              type="text"
              required
              data-testid="registration-first-name"
            />
          </div>

          <div class="uc-form-element">
            <label class="uc-form-label" for="registration-last-name">
              {{ $t('login.mobile.registrationForm.lastName') }}
              <span class="uc-form-required">{{
                $t('login.mobile.registrationForm.requiredHint')
              }}</span>
            </label>
            <input
              id="registration-last-name"
              v-model="registration.lastName"
              class="uc-form-text-input"
              type="text"
              required
              data-testid="registration-last-name"
            />
          </div>

          <!-- Q-006: email is OPTIONAL for an OTP-first account. -->
          <div class="uc-form-element">
            <label class="uc-form-label" for="registration-email">
              {{ $t('login.mobile.registrationForm.emailOptional') }}
            </label>
            <input
              id="registration-email"
              v-model="registration.email"
              class="uc-form-text-input"
              type="email"
              data-testid="registration-email"
            />
          </div>

          <label class="uc-form-checkbox">
            <input
              v-model="registration.terms"
              type="checkbox"
              data-testid="registration-terms"
            />
            <span>{{ $t('login.mobile.registrationForm.terms') }}</span>
          </label>
          <!-- Sibling, not a child of the label -- see the note on the role
               onboarding terms link below. -->
          <i18n path="login.mobile.termsLegal" tag="p" class="uc-form-subtext">
            <router-link
              :to="`/${$i18n.locale}/legal`"
              target="_blank"
              class="uc-link"
              data-testid="registration-legal-link"
              >{{ $t('studentPartnerSignUp.agreementLink') }}</router-link
            >
          </i18n>

          <button
            type="submit"
            class="uc-form-button"
            data-testid="registration-submit"
            :disabled="!canSubmitRegistration"
          >
            {{ $t('login.mobile.registrationForm.submit') }}
          </button>
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="registration-form-back-button"
              @click="mobileState = 'REGISTRATION_REQUIRED'"
            >
              {{ $t('login.mobile.registrationForm.back') }}
            </button>
          </p>
        </form>

        <!--
          FR-07, SR-09: generic, discloses no account count/identity/role,
          never retried automatically.
        -->
        <div
          v-else-if="mobileState === 'ACCOUNT_CONFLICT'"
          data-testid="account-conflict"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.accountConflict.body') }}
          </p>
          <button
            type="button"
            class="uc-form-button"
            data-testid="account-conflict-back-button"
            @click="resetMobileFlow"
          >
            {{ $t('login.mobile.backToPhone') }}
          </button>
        </div>

        <!--
          Task 11 / STEP-12 (Q-011): the requested role is missing from this
          ALREADY AUTHENTICATED account. The session established a moment ago
          is untouched -- this offers to add the role to the same identity.
          Deliberately not a link to /sign-up and deliberately no second OTP.
        -->
        <form
          v-else-if="mobileState === 'ROLE_ONBOARDING_REQUIRED'"
          data-testid="role-onboarding-required"
          @submit.prevent="submitRoleOnboarding"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.roleOnboardingRequired.body') }}
          </p>
          <label class="uc-form-checkbox">
            <input
              v-model="roleOnboardingTerms"
              type="checkbox"
              data-testid="role-onboarding-terms"
            />
            <span>{{ $t('login.mobile.roleOnboardingRequired.terms') }}</span>
          </label>
          <!--
            Task 12 / STEP-05 (section 11). The legal destination is the app's
            OWN existing /:lang/legal route, linked the way FormPageTemplate
            already links it. Deliberately a SIBLING of the label rather than a
            child: an anchor inside a <label> toggles the checkbox on click, so
            nesting it would make reading the agreement silently accept it.
          -->
          <i18n path="login.mobile.termsLegal" tag="p" class="uc-form-subtext">
            <router-link
              :to="`/${$i18n.locale}/legal`"
              target="_blank"
              class="uc-link"
              data-testid="role-onboarding-legal-link"
              >{{ $t('studentPartnerSignUp.agreementLink') }}</router-link
            >
          </i18n>
          <button
            type="submit"
            class="uc-form-button"
            data-testid="role-onboarding-submit"
            :disabled="!roleOnboardingTerms || isOnboardingRole"
          >
            {{ roleOnboardingSubmitLabel }}
          </button>
          <!--
            Task 12 / STEP-05 (section 8). Without this the screen was a trap:
            the only way forward was accepting a role the user may have reached
            by mistake, and the only way out was closing the tab -- on an
            account that is ALREADY signed in.

            Subordinate to the primary CTA on purpose, and it declines a role
            rather than the login: no logout, no role mutation, no server call.
            It drops the pending intent and hands over to the dashboard, where
            STEP-04's rules decide the acting role.
          -->
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="role-onboarding-skip-button"
              :disabled="isOnboardingRole"
              @click="continueWithCurrentAccount"
            >
              {{ $t('login.mobile.roleOnboardingRequired.skip') }}
            </button>
          </p>
        </form>

        <!--
          The fail-safe branch (FR-08), reachable for the first time in
          STEP-12: role attachment was declined in a controlled way. The user
          REMAINS AUTHENTICATED -- this declines a role, not the login.
        -->
        <div
          v-else-if="mobileState === 'ROLE_ONBOARDING_UNSUPPORTED'"
          data-testid="role-onboarding-unsupported"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.roleOnboardingUnsupported.body') }}
          </p>
          <!--
            Task 12 / STEP-05 (section 8). This state had no action of any
            kind, so a user whose role attachment was declined was left on a
            dead screen -- while authenticated. Same escape as above: the role
            is declined, the session is not.
          -->
          <button
            type="button"
            class="uc-form-button"
            data-testid="role-onboarding-unsupported-continue"
            @click="continueWithCurrentAccount"
          >
            {{ $t('login.mobile.roleOnboardingUnsupported.continue') }}
          </button>
        </div>

        <!--
          Task 12 / STEP-03 (section 4). THE shared mobile-auth error region.
          Exactly one node, for every state.

          Before this, the alert lived inside the PHONE_ENTRY and OTP_ENTRY
          branches only. `submitMobileRegistration` and `submitRoleOnboarding`
          both set `mobileError` on a controlled failure (a 422 in particular),
          and it rendered nowhere at all -- the user saw a dead submit button
          and no explanation. That was the single highest-value defect in the
          flow.

          It sits after the branch container rather than inside each branch, so
          there is one node instead of four and the same message can never
          appear twice. Because every state ends with its primary CTA, that
          places the message directly beneath the action that produced it in
          every state -- adjacency without duplication.

          role="alert" carries an implicit aria-live="assertive", so the message
          is announced when it appears; the phone and OTP controls point at it
          through aria-describedby.
        -->
        <div
          v-if="mobileError"
          id="mobileAuthError"
          class="alert alert-danger"
          role="alert"
          data-testid="mobile-error"
        >
          {{ mobileError }}
        </div>

        <!--
          Task 12 / STEP-05 (section 9). This was rendered in EVERY mobile
          state. Past OTP entry it is not a secondary action any more, it is a
          way to throw away work: a user mid-registration holds a verified
          grant, and one mid-role-onboarding is already signed in -- neither
          has a password to fall back to, and both would lose what they have.
          Presentation only; the email/password path itself is untouched and
          still reachable from the two states where it makes sense.
        -->
        <p
          v-if="showEmailEscape"
          class="uc-form-secondary-action"
          data-testid="switch-to-email-action"
        >
          <button
            type="button"
            class="uc-link-button"
            data-testid="switch-to-email-button"
            @click="showEmailLogin = true"
          >
            {{ $t('login.mobile.switchToEmailButton') }}
          </button>
        </p>
      </div>

      <form
        v-if="!mobileFlowEnabled || showEmailLogin"
        data-testid="email-login-form"
      >
        <div class="uc-form-element">
          <label for="inputEmail" class="uc-form-label">{{
            $t('login.emailLabel')
          }}</label>
          <input
            id="inputEmail"
            class="uc-form-text-input"
            type="email"
            :placeholder="$t('login.emailPlaceholder')"
            v-model="credentials.email"
            required
            :autofocus="!mobileFlowEnabled"
          />
        </div>

        <div class="uc-form-element">
          <label for="inputPassword" class="uc-form-label">{{
            $t('login.passwordLabel')
          }}</label>
          <input
            id="inputPassword"
            class="uc-form-text-input"
            type="password"
            :placeholder="$t('login.passwordPlaceholder')"
            v-model="credentials.password"
            required
          />
          <router-link
            :to="`/${$i18n.locale}/resetpassword`"
            class="uc-link subtext"
          >
            {{ $t('login.forgotPassword') }}
          </router-link>
        </div>

        <button
          class="uc-form-button"
          type="submit"
          @click.prevent="signIn"
          :disabled="!isValidForm || isLoggingIn"
        >
          {{ $t('login.signInButton') }}
        </button>

        <p v-if="mobileFlowEnabled" class="uc-form-secondary-action">
          <button
            type="button"
            class="uc-link-button"
            data-testid="switch-to-mobile-button"
            @click="showEmailLogin = false"
          >
            {{ $t('login.mobile.switchToMobileButton') }}
          </button>
        </p>
      </form>

      <!--
        Hotfix / mobile-registration-entry: the legacy /sign-up link must
        never be reachable from the mobile flow -- it leads into the legacy
        password-based SignupView, not the Task-11 OTP registration state
        machine above. Registration IS the mobile flow itself, so at
        PHONE_ENTRY there is nothing to link to; past PHONE_ENTRY the user
        already holds an issued OTP or verified grant, so no exit into
        legacy signup is offered (the existing back controls in each state
        remain authoritative). Only the email-fallback screen and the
        flag-off legacy screen still offer a sign-up action.
      -->
      <p
        v-if="
          mobileFlowEnabled && !showEmailLogin && mobileState === 'PHONE_ENTRY'
        "
        class="uc-form-text"
        data-testid="mobile-signup-hint"
      >
        {{ $t('login.mobile.signUpHint') }}
      </p>
      <i18n
        v-else-if="mobileFlowEnabled && showEmailLogin"
        path="login.needAccount"
        tag="p"
        class="uc-form-text"
      >
        <button
          type="button"
          class="uc-link-button"
          data-testid="switch-to-mobile-signup"
          @click="signUpAction"
        >
          {{ $t('login.signUp') }}
        </button>
      </i18n>
      <i18n
        v-else-if="!mobileFlowEnabled"
        path="login.needAccount"
        tag="p"
        class="uc-form-text"
      >
        <router-link class="uc-link" :to="`/${$i18n.locale}/sign-up`">{{
          $t('login.signUp')
        }}</router-link>
      </i18n>
    </div>
  </form-page-template>
</template>

<script>
import AnalyticsService from '@/services/AnalyticsService'
import AuthService from '@/services/AuthService'
import FormPageTemplate from '@/components/FormPageTemplate.vue'
import SimpleOtpInput from 'vue-simple-otp-input'
import VuePhoneNumberInput from 'vue-phone-number-input'
import { EVENTS } from '@/consts'
import config from '@/config'
import i18n from '@/i18n'

// Task 12 / STEP-03 (section 9). Client-side resend throttle only. The backend
// sends no Retry-After and no OTP expiry, so this number is a UI affordance --
// it must never be described to the user as an authoritative unlock time, and
// backend rate limiting stays the real control.
const RESEND_COOLDOWN_SECONDS = 120

export default {
  components: {
    FormPageTemplate,
    SimpleOtpInput,
    VuePhoneNumberInput,
  },
  created() {
    this.$store.dispatch('app/hideNavigation')
    localStorage.removeItem('isSSOSignUpRedirect')
  },
  computed: {
    isValidForm() {
      const { email, password } = this.credentials
      return email && password
    },
    // CR-07, CR-03: discoverability only -- a build-time value, never a
    // security boundary. The backend gate is authoritative; when it is off
    // the /auth/mobile/* calls this view makes fail safely regardless of
    // this flag (mapMobileError's 404 branch).
    mobileFlowEnabled() {
      return config.mobilePhoneAuthEnabled
    },
    isValidPhone() {
      return Boolean(this.phoneInputInfo && this.phoneInputInfo.isValid)
    },
    isValidOtp() {
      return /^\d{6}$/.test(this.otpCode)
    },
    // Read-only orientation only. The submitted body never carries a phone --
    // the backend re-reads the verified number from the registration grant.
    phoneDisplay() {
      return this.phoneInputInfo?.formatNational || this.phone
    },
    canSubmitRegistration() {
      const { firstName, lastName, terms } = this.registration
      return (
        Boolean(firstName.trim()) &&
        Boolean(lastName.trim()) &&
        terms === true &&
        !this.isRegistering
      )
    },
    // Task 12 / STEP-03. Attributes handed to each OTP box. `one-time-code`
    // plus a numeric inputmode is what makes the OS keyboard and the iOS SMS
    // autofill suggestion behave on a phone.
    otpInputProps() {
      return {
        inputmode: 'numeric',
        autocomplete: 'one-time-code',
        'aria-invalid': this.mobileError ? 'true' : null,
      }
    },
    // Guards the resend control: never while a request is in flight, never
    // during the cooldown.
    canResendCode() {
      return (
        !this.isSubmitting && !this.isResending && this.resendCooldown === 0
      )
    },
    // Task 12 / STEP-02 (A). The card's single <h1>, derived from the state the
    // user is actually in rather than a constant.
    //
    // Reads only existing state -- it decides nothing and changes no transition.
    // Most branches reuse the heading string each state already owned as its own
    // <h2>; those <h2>s are removed, so this reparents them rather than
    // duplicating them. Only the three states that never had a heading of their
    // own (phone entry, OTP entry, pending) needed new keys.
    pageHeading() {
      if (!this.mobileFlowEnabled || this.showEmailLogin) {
        return this.$t('login.header')
      }

      switch (this.mobileState) {
        case 'OTP_ENTRY':
          return this.$t('login.mobile.heading.otpEntry')
        case 'RESOLVING':
        case 'SESSION_ESTABLISHING':
          return this.$t('login.mobile.heading.pending')
        case 'REGISTRATION_REQUIRED':
          return this.$t('login.mobile.registrationRequired.header')
        case 'REGISTRATION_FORM':
          return this.registrationRole === 'volunteer'
            ? this.$t('login.mobile.registrationForm.volunteerHeader')
            : this.$t('login.mobile.registrationForm.studentHeader')
        case 'ACCOUNT_CONFLICT':
          return this.$t('login.mobile.accountConflict.header')
        // Task 12 / STEP-05 (section 7): name the role being added rather than
        // saying "this role" about a role the screen never identifies.
        case 'ROLE_ONBOARDING_REQUIRED':
          return this.roleIntent === 'student'
            ? this.$t('login.mobile.roleOnboardingRequired.headerStudent')
            : this.$t('login.mobile.roleOnboardingRequired.headerVolunteer')
        case 'ROLE_ONBOARDING_UNSUPPORTED':
          return this.$t('login.mobile.roleOnboardingUnsupported.header')
        default:
          return this.$t('login.mobile.heading.phoneEntry')
      }
    },

    // Task 12 / STEP-06 (C). vue-phone-number-input ships English defaults for
    // its own internal labels, so the Persian card rendered "Country code" and
    // "Phone number" in English. The component already supports a
    // `translations` prop which it merges over those defaults, so this is the
    // component's own documented API -- no fork, no replacement, and English
    // is unaffected because the keys resolve through the active locale.
    phoneInputTranslations() {
      return {
        countrySelectorLabel: this.$t(
          'login.mobile.phoneInput.countrySelectorLabel'
        ),
        countrySelectorError: this.$t(
          'login.mobile.phoneInput.countrySelectorError'
        ),
        phoneNumberLabel: this.$t('login.mobile.phoneInput.phoneNumberLabel'),
        example: this.$t('login.mobile.phoneInput.example'),
      }
    },

    // Task 12 / STEP-05 (section 4). States the current intent in words so the
    // chip fill is never the only carrier of the selection.
    roleIntentNotice() {
      if (this.roleIntent === 'student') {
        return this.$t('login.mobile.roleIntent.selectedStudent')
      }
      if (this.roleIntent === 'volunteer') {
        return this.$t('login.mobile.roleIntent.selectedVolunteer')
      }
      return null
    },

    // Task 12 / STEP-05 (section 9). The email/password fallback is a sensible
    // secondary action only while the user still has nothing to lose by taking
    // it -- before a grant is issued, or while one is merely pending.
    showEmailEscape() {
      return (
        this.mobileState === 'PHONE_ENTRY' || this.mobileState === 'OTP_ENTRY'
      )
    },

    // Task 12 / STEP-05 (section 7). Names the role actually being added, so
    // the screen stops saying "this role" about a role it never identifies.
    roleOnboardingSubmitLabel() {
      return this.roleIntent === 'student'
        ? this.$t('login.mobile.roleOnboardingRequired.submitStudent')
        : this.$t('login.mobile.roleOnboardingRequired.submitVolunteer')
    },

    // Task 12 / STEP-05 (section 10).
    registrationLead() {
      return this.registrationRole === 'volunteer'
        ? this.$t('login.mobile.registrationForm.volunteerLead')
        : this.$t('login.mobile.registrationForm.studentLead')
    },
  },
  data() {
    let error
    if (this.$route.query['401'] === 'true') {
      error = this.$root.$t('login.error.401')
    }
    if (this.$route.query['400'] === 'true') {
      AnalyticsService.captureEvent(
        EVENTS.USER_DOES_NOT_HAVE_LINKED_GOOGLE_ACCOUNT
      )
      error = this.$root.$t('login.error.400')
    }
    this.isLoggingIn = false
    return {
      credentials: {
        email: '',
        password: '',
      },
      error,
      isLoggingIn: false,

      // Task 11 / STEP-08 mobile OTP login state -- in-memory only.
      showEmailLogin: false,
      mobileState: 'PHONE_ENTRY',
      phone: '',
      phoneInputInfo: {},
      otpCode: '',
      challengeId: null,
      ownershipGrantId: null,
      registrationGrantId: null,
      mobileError: null,
      // Task 11 / STEP-12 (Q-011): the role the person came here to use. Read
      // from the route (?role=student|volunteer) so a role-specific entry
      // point carries its intent through login. Null means "no intent", which
      // is honoured literally -- a dual-role user with no intent is ASKED
      // rather than sent to whichever dashboard a boolean happened to pick.
      roleIntent:
        this.$route.query.role === 'student' ||
        this.$route.query.role === 'volunteer'
          ? this.$route.query.role
          : null,
      isOnboardingRole: false,
      roleOnboardingTerms: false,
      // Task 11 new-user registration. `registrationRole` is the INITIAL role
      // whose profile will be created for a brand-new account -- distinct from
      // `roleIntent` (which role an EXISTING account was entered for) and from
      // STEP-12's activeRole (which role an existing identity is acting as).
      registrationRole: null,
      isRegistering: false,
      registration: {
        firstName: '',
        lastName: '',
        email: '',
        terms: false,
      },
      // Single-flight guard: set synchronously before the first await in
      // every mobile transition method and checked at each method's entry,
      // so at most one send/confirm/resolve/login round trip is ever in
      // flight -- a stale response cannot arrive after a newer transition
      // has already started, because a newer one cannot start until this is
      // cleared (RR-05, section 16).
      isSubmitting: false,

      // Task 12 / STEP-03. Resend state. `resendCooldown` counts seconds down
      // to zero and gates the control; `resendNotice` is the visible "we sent
      // another one" confirmation. Its in-flight guard is deliberately separate
      // from `isSubmitting` so a resend is never mistaken for a state
      // transition. The interval id itself is kept off `data` -- it is not
      // render state and must not be made reactive.
      isResending: false,
      resendCooldown: 0,
      resendNotice: null,
    }
  },
  watch: {
    mobileState(newState) {
      if (newState === 'OTP_ENTRY') {
        this.focusOtpInput()
      }
    },
  },
  // Task 12 / STEP-03 (section 10). Give the first field focus so a returning
  // user can start typing their number immediately.
  mounted() {
    if (this.mobileFlowEnabled && !this.showEmailLogin) this.focusPhoneInput()
  },
  // The cooldown interval outlives a route change unless it is cleared here.
  beforeDestroy() {
    this.stopResendCooldown()
  },
  methods: {
    signIn() {
      if (!this.isValidForm) return
      this.isLoggingIn = true
      AuthService.login({
        email: this.credentials.email,
        password: this.credentials.password,
      })
        .then(data => {
          this.$store.commit('user/setUser', data.user)
          this.$router.push(
            this.$route.query.redirect || `/${i18n.locale}/dashboard`
          )
        })
        .catch(() => {
          this.error = this.$root.$t('login.error.authServiceError')
        })
        .finally(() => {
          this.isLoggingIn = false
        })
    },

    onPhoneInputUpdate(phoneInputInfo) {
      this.phoneInputInfo = phoneInputInfo
    },

    // --- Task 12 / STEP-03: focus management -------------------------------
    //
    // Both helpers reach for a real <input> inside a third-party component, so
    // both are defensive: a missing ref or a component that changed its markup
    // must never throw inside a state transition.

    focusPhoneInput() {
      this.$nextTick(() => {
        const root =
          this.$refs.mobilePhoneInput && this.$refs.mobilePhoneInput.$el
        const input = root && root.querySelector('input[type="tel"], input')
        if (input && typeof input.focus === 'function') input.focus()
      })
    },

    // SimpleOtpInput renders one <input> per digit; focus belongs on the first
    // empty box, which after a clear is simply the first one.
    focusOtpInput() {
      this.$nextTick(() => {
        const root = this.$refs.mobileOtpInput && this.$refs.mobileOtpInput.$el
        const input = root && root.querySelector('input')
        if (input && typeof input.focus === 'function') input.focus()
      })
    },

    // --- Task 12 / STEP-03: resend + client-side cooldown ------------------

    startResendCooldown(seconds = RESEND_COOLDOWN_SECONDS) {
      this.stopResendCooldown()
      this.resendCooldown = seconds
      this.resendTimerId = setInterval(() => {
        this.resendCooldown -= 1
        if (this.resendCooldown <= 0) this.stopResendCooldown()
      }, 1000)
    },

    stopResendCooldown() {
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId)
        this.resendTimerId = null
      }
      this.resendCooldown = 0
    },

    // Re-issues the SAME challenge request against the SAME number. No new
    // endpoint, no new contract: this is `sendMobileChallenge` again, which is
    // exactly what the user pressing "resend" means. The old challengeId is
    // replaced by the new one, so the previously typed code is cleared rather
    // than left to be confirmed against a challenge that no longer applies.
    async resendMobileCode() {
      if (!this.canResendCode || !this.isValidPhone) return
      this.isResending = true
      this.mobileError = null
      this.resendNotice = null
      try {
        const { challengeId } = await AuthService.sendMobileChallenge(
          this.phoneInputInfo.e164
        )
        this.challengeId = challengeId
        this.otpCode = ''
        this.resendNotice = this.$t('login.mobile.resendSent')
        this.startResendCooldown()
        this.focusOtpInput()
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'send')
      } finally {
        this.isResending = false
      }
    },

    // Task 12 / STEP-05 (section 4). The producer for `roleIntent`, which the
    // Task-11 contract has consumed since STEP-12 but which nothing could
    // previously set. Toggling: pressing the active chip clears the intent, so
    // a mis-tap is recoverable and "no intent" stays genuinely reachable.
    //
    // Writes intent ONLY. It grants nothing: whether the role can actually be
    // used is still decided by `roles` from the server in
    // establishMobileSession, exactly as before.
    toggleRoleIntent(role) {
      this.roleIntent = this.roleIntent === role ? null : role
    },

    // Task 12 / STEP-05 (section 8). Declines the ROLE, never the session.
    //
    // The account is already authenticated by the time either onboarding state
    // renders, so there is nothing to log out of and nothing to re-verify. No
    // role is added or removed, no store action is dispatched and no request is
    // made -- the pending intent is simply dropped, which is what makes the
    // dashboard treat this as an ordinary arrival. A dual-role account with no
    // acting role is then asked by DashboardView under STEP-04's rules rather
    // than being resolved here.
    continueWithCurrentAccount() {
      this.roleIntent = null
      this.mobileError = null
      this.$router.push(`/${i18n.locale}/dashboard`)
    },

    // Full reset: an explicit user "start over" action, so the error is
    // cleared along with the rest of the flow's state.
    resetMobileFlow() {
      this.returnToPhoneEntry()
      this.mobileError = null
    },

    // Hotfix / mobile-registration-entry: the email-fallback screen's
    // sign-up action returns to the mobile flow instead of navigating to
    // legacy /sign-up -- registration for a new user IS the mobile flow.
    signUpAction() {
      this.showEmailLogin = false
      this.resetMobileFlow()
    },

    // Internal reset used after a failure: returns to phone entry without
    // clobbering the mobileError the caller just set.
    returnToPhoneEntry() {
      this.mobileState = 'PHONE_ENTRY'
      this.otpCode = ''
      this.challengeId = null
      this.ownershipGrantId = null
      this.registrationGrantId = null
      // Task 11 new-user registration: the half-filled form and the chosen
      // initial role belong to the grant that is being discarded here, so they
      // must not survive into a fresh attempt with a different number.
      this.registrationRole = null
      this.registration = {
        firstName: '',
        lastName: '',
        email: '',
        terms: false,
      }
      // Task 12 / STEP-03: the cooldown belonged to the challenge just
      // discarded. Leaving it running would block the resend control on a
      // fresh attempt with a different number.
      this.stopResendCooldown()
      this.resendNotice = null
    },

    async sendMobileCode() {
      if (this.isSubmitting || !this.isValidPhone) return
      this.isSubmitting = true
      this.mobileError = null
      try {
        const { challengeId } = await AuthService.sendMobileChallenge(
          this.phoneInputInfo.e164
        )
        this.challengeId = challengeId
        this.otpCode = ''
        this.resendNotice = null
        // Task 12 / STEP-03: a code has just gone out, so the resend throttle
        // starts here rather than only after the first manual resend.
        this.startResendCooldown()
        this.mobileState = 'OTP_ENTRY'
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'send')
      } finally {
        this.isSubmitting = false
      }
    },

    async confirmMobileCode() {
      if (this.isSubmitting || !this.isValidOtp) return
      this.isSubmitting = true
      this.mobileError = null
      try {
        const {
          ownershipGrantId,
        } = await AuthService.confirmMobileVerification({
          challengeId: this.challengeId,
          code: this.otpCode,
        })
        this.ownershipGrantId = ownershipGrantId
        this.otpCode = ''
        await this.resolveMobileIdentity()
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'confirm')
        // A wrong/expired code does not invalidate the still-issued
        // challenge (Task 04 precedent) -- stay on OTP_ENTRY so it can be
        // retried. Only a "feature unavailable" response returns to phone
        // entry, since there is nothing left to retry against.
        if (err && err.status === 404) {
          this.returnToPhoneEntry()
        } else {
          // Task 12 / STEP-03 (section 10). Retry has to be immediate: empty
          // the boxes and put the caret back in the first one, so the user is
          // never left hunting for focus or deleting six digits by hand.
          this.otpCode = ''
          this.resendNotice = null
          this.focusOtpInput()
        }
      } finally {
        this.isSubmitting = false
      }
    },

    async resolveMobileIdentity() {
      this.mobileState = 'RESOLVING'
      try {
        const result = await AuthService.resolveMobileIdentity(
          this.ownershipGrantId
        )
        if (result.outcome === 'EXISTING_ACCOUNT') {
          await this.establishMobileSession()
        } else if (result.outcome === 'REGISTRATION_REQUIRED') {
          // STEP-09/10/11 boundary: retained only for a future step: no
          // registration call is made with it in this slice.
          this.registrationGrantId = result.registrationGrantId

          // Task 12 / STEP-05 (section 6). A new user who already said why
          // they were here is not asked the identical question a second time;
          // their intent seeds `registrationRole` and the form opens directly.
          //
          // `registrationRole` stays the sole authority over which account is
          // created -- intent only supplies its initial value, and the form's
          // "choose a different role" action still returns to the full choice,
          // so nothing is locked in. With no intent, REGISTRATION_REQUIRED is
          // reached exactly as before.
          if (this.roleIntent) {
            this.registrationRole = this.roleIntent
            this.mobileState = 'REGISTRATION_FORM'
          } else {
            this.mobileState = 'REGISTRATION_REQUIRED'
          }
        } else if (result.outcome === 'ACCOUNT_CONFLICT') {
          this.mobileState = 'ACCOUNT_CONFLICT'
        } else {
          this.mobileError = this.$t('login.mobile.error.generic')
          this.returnToPhoneEntry()
        }
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'resolve')
        this.returnToPhoneEntry()
      }
    },

    async establishMobileSession() {
      this.mobileState = 'SESSION_ESTABLISHING'
      try {
        const result = await AuthService.establishMobileSession(
          this.ownershipGrantId
        )
        if (result && result.authenticated) {
          // Task 11 / STEP-12 (Q-011): the session now exists and is NOT
          // revoked by anything below. If the person came for a role this
          // account does not hold, offer to attach it to THIS account -- never
          // a redirect to /sign-up, which would create a second identity for
          // the same human, and never a second OTP, because the session they
          // just established is already the ownership proof.
          await this.$store.dispatch('user/fetchUser')
          const roles = this.$store.getters['user/roles']

          if (this.roleIntent && !roles.includes(this.roleIntent)) {
            this.mobileState = 'ROLE_ONBOARDING_REQUIRED'
            return
          }
          if (this.roleIntent) {
            await this.$store.dispatch('user/setActiveRole', this.roleIntent)
          }

          // Q-013 owner decision for this slice: existing-account mobile
          // login always goes to the locale-aware dashboard. Deliberately
          // does not consult $route.query.redirect -- that behaviour is
          // password-login's, and Task 11 must not widen it (SR-14). A
          // dual-role user arriving with no intent is asked by the dashboard
          // itself (roleSelectionRequired), not guessed at here.
          this.$router.push(`/${i18n.locale}/dashboard`)
        } else {
          this.mobileError = this.$t('login.mobile.error.generic')
          this.returnToPhoneEntry()
        }
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'login')
        this.returnToPhoneEntry()
      }
    },

    // Task 11 new-user registration: pick which account to CREATE. This runs
    // before any account exists, so it is not role onboarding and not an
    // acting-role switch -- nothing is attached to an existing identity here.
    chooseRegistrationRole(role) {
      this.registrationRole = role
      this.mobileError = null
      this.mobileState = 'REGISTRATION_FORM'
    },

    // Task 11 / STEP-10 (student) and STEP-11 (volunteer). The verified
    // registrationGrantId held since resolution is the only identity
    // authority: no second OTP, no new challenge, and no phone/password/
    // userId in the body (AuthService rebuilds it from an allowlist).
    async submitMobileRegistration() {
      if (this.isRegistering || !this.canSubmitRegistration) return
      this.isRegistering = true
      this.mobileError = null
      try {
        const payload = {
          registrationGrantId: this.registrationGrantId,
          firstName: this.registration.firstName.trim(),
          lastName: this.registration.lastName.trim(),
          terms: this.registration.terms,
          email: this.registration.email.trim(),
        }

        const result =
          this.registrationRole === 'volunteer'
            ? await AuthService.registerMobileVolunteer(payload)
            : await AuthService.registerMobileStudent(payload)

        // The backend establishes the session itself on success, so there is
        // nothing to log in to here -- only state to load.
        if (result && result.authenticated) {
          await this.$store.dispatch('user/fetchUser')
          await this.$store.dispatch(
            'user/setActiveRole',
            this.registrationRole
          )
          this.$router.push(`/${i18n.locale}/dashboard`)
          return
        }

        // A data-integrity conflict discovered at commit time. Same fail-safe
        // shape as resolution's own ACCOUNT_CONFLICT: terminal, discloses
        // nothing, never retried automatically.
        if (result && result.outcome === 'ACCOUNT_CONFLICT') {
          this.mobileState = 'ACCOUNT_CONFLICT'
          return
        }

        this.mobileError = this.$t('login.mobile.error.generic')
      } catch (err) {
        // Controlled validation failures (422) keep the user on the form with
        // the SAME grant, which the backend still considers recoverable. Only
        // an invalid/expired grant (401) or the rollout gate (404) sends them
        // back to the start, because there is nothing left to retry against.
        this.mobileError = this.mapMobileError(err, 'register')
        if (err && (err.status === 401 || err.status === 404)) {
          this.returnToPhoneEntry()
        }
      } finally {
        this.isRegistering = false
      }
    },

    // Task 11 / STEP-12 (Q-011): attach the requested role to the account the
    // user is ALREADY signed into. On failure the session survives untouched
    // and only the role attachment is declined -- ROLE_ONBOARDING_UNSUPPORTED
    // is the fail-safe branch, wired up here for the first time.
    async submitRoleOnboarding() {
      if (this.isOnboardingRole) return
      this.isOnboardingRole = true
      this.mobileError = null
      try {
        const payload = await this.$store.dispatch('user/onboardRole', {
          role: this.roleIntent,
          data: { terms: this.roleOnboardingTerms },
        })

        const attached =
          payload.outcome === 'ROLE_ATTACHED' ||
          payload.outcome === 'ROLE_COMPLETED' ||
          payload.outcome === 'ROLE_ALREADY_HELD'

        if (attached) {
          // Reconcile with the server BEFORE navigating. The dashboard guard
          // reads activeRole the moment this push lands, so it must not be
          // allowed to run against an optimistic local value.
          await this.$store.dispatch(
            'user/reconcileAfterRoleOnboarding',
            this.roleIntent
          )
          this.$router.push(`/${i18n.locale}/dashboard`)
          return
        }
        this.mobileState = 'ROLE_ONBOARDING_UNSUPPORTED'
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'roleOnboarding')
      } finally {
        this.isOnboardingRole = false
      }
    },

    // Maps every backend response into the small controlled set from the
    // STEP-08 authorization -- never a raw backend error body, and a 404
    // (rollout gate off) is always "feature unavailable", never treated as
    // an account-existence signal (SR-09).
    mapMobileError(err, stage) {
      const status = err && err.status
      if (status === 404) {
        return this.$t('login.mobile.error.unavailable')
      }
      // Task 12 / STEP-03 (section 5). Every mobile auth route is rate
      // limited, and a 429 previously fell through to the generic "please try
      // again" -- which tells a rate-limited user to do the one thing that
      // makes their situation worse. Stage-independent on purpose: the meaning
      // is the same wherever the limiter fires. No wait duration is claimed,
      // because the backend sends no Retry-After to claim one from.
      if (status === 429) {
        return this.$t('login.mobile.error.tooManyRequests')
      }
      if (stage === 'send' && status === 422) {
        return this.$t('login.mobile.error.invalidPhone')
      }
      if ((stage === 'confirm' || stage === 'login') && status === 401) {
        return this.$t('login.mobile.error.invalidOtp')
      }
      return this.$t('login.mobile.error.generic')
    },
  },
}
</script>

<style lang="scss" scoped>
// ---------------------------------------------------------------------------
// Task 12 / STEP-06 — auth card composition (notes E, F, G)
//
// Scoped overrides of the SHARED `.uc-form` rules in scss/layout/_form.scss.
// Deliberately scoped rather than edited globally: `.uc-form` is also the
// signup, verification, reset-password and partner-signup shell, and none of
// those were reviewed in this task. LoginView's own root carries the class, so
// the scope attribute confines every rule below to this view.
// ---------------------------------------------------------------------------
.uc-form {
  // F. The card sat flat on the illustrated desktop background with no border
  // or shadow, so it read as pasted on rather than placed. A soft shadow in
  // the existing blue-grey family lifts it without introducing a new theme.
  box-shadow: 0 4px 24px rgba(52, 52, 64, 0.08);
  padding: 32px 28px;

  // E. The shared rule forces `min-height: 500px` on every state. The short
  // states (pending, conflict, declined role) do not have 500px of content, so
  // the card grew a block of empty white below the action instead of ending.
  // Letting it size to its content is what makes each state look deliberate.
  min-height: 0;

  @include breakpoint-below('small') {
    padding: 24px 20px;
  }

  @include breakpoint-below('tiny') {
    // The shared rule pads 100px here. That is clearance for the FIXED footer
    // (min-height 100px below 'tiny'), so it cannot simply be removed -- the
    // footer would sit on top of the last control. Kept as clearance only,
    // trimmed to the footer's actual height plus a small gap.
    padding: 20px 16px;
    padding-bottom: 108px;

    // Below 'tiny' the card loses its radius and goes edge to edge -- it IS
    // the page surface here, not a card floating on a background. Dropping
    // `min-height` therefore left a bare strip of page backdrop between the
    // end of a short state and the fixed footer. Reaching the footer keeps the
    // surface continuous; the content still sizes itself, so nothing is
    // padded out to fill it.
    //
    // 92px is the logo block above the card: a 130px-wide render of a 1280x618
    // asset is ~63px tall, plus its 20px/8px padding.
    min-height: calc(100vh - 92px);
  }
}

.alert {
  margin: 25px 0;
}

// G. The heading is the anchor of every state; the shared 26px is heavy beside
// the compact mobile card and wrapped the longer role headings onto three
// lines. Slightly tighter, with real line-height so wrapped headings stay
// readable.
.uc-form-header {
  font-size: 22px;
  line-height: 1.45;

  @include breakpoint-above('small') {
    font-size: 25px;
  }
}

.uc-form-text {
  text-align: center;
}

.uc-link.subtext {
  margin: 2px 0 0 10px;
}

// ---------------------------------------------------------------------------
// Task 12 / STEP-02 — visual foundation
//
// One card, one visual language, one action hierarchy. Every state follows the
// same vertical order: heading -> lead copy -> content -> error -> primary CTA
// -> secondary action. These rules exist to make that order look deliberate;
// none of them changes what any state does.
// ---------------------------------------------------------------------------

// Explanatory copy directly under the heading. `.uc-form-text` was doing this
// job with a 25px top margin meant for footer links, which left a gap between
// the heading and the sentence explaining it.
.uc-form-lead {
  color: $c-secondary-grey;
  font-size: 15px;
  line-height: 1.6;
  margin: 8px 0 0 0;
}

// The phone number, wherever it is shown back to the user. A digit run is not
// RTL text: see the RTL block at the end of this file.
.phone-inline {
  color: $c-soft-black;
  font-weight: 500;
}

p.phone-inline {
  margin: 2px 0 0 0;
}

// Secondary actions are subordinate to the primary CTA: centred, smaller, and
// never competing for the same visual weight.
.uc-form-secondary-action {
  font-size: 14px;
  margin: 16px 0 0 0;
  text-align: center;
}

// Terms and similar consent rows. Previously `.uc-form-text`, whose 25px top
// margin and block layout left the checkbox floating away from its own label.
// `gap` is deliberately avoided in this file: flexbox gap is unsupported on
// iOS Safari below 14.1, and this is the mobile-first entry point of the
// product. Margins are used instead so the layout cannot silently collapse on
// an older phone.
.uc-form-checkbox {
  align-items: flex-start;
  display: flex;
  font-size: 14px;
  line-height: 1.5;
  margin: 20px 0 0 0;

  input[type='checkbox'] {
    flex: 0 0 auto;
    height: 18px;
    // Optically aligns the box with the first line of a wrapping label.
    margin: 2px 8px 0 0;
    width: 18px;
  }
}

html.is-rtl .uc-form-checkbox input[type='checkbox'] {
  margin: 2px 0 0 8px;
}

.uc-form-pending {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 0;
  text-align: center;
}

.uc-form-spinner {
  animation: uc-form-spin 0.8s linear infinite;
  border: 3px solid $c-border-grey;
  border-radius: 50%;
  border-top-color: $c-information-blue;
  display: block;
  height: 32px;
  width: 32px;
}

@keyframes uc-form-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .uc-form-spinner {
    animation-duration: 2.4s;
  }
}

// --- Role intent -----------------------------------------------------------
// Compact and deliberately quiet: this sits above the primary CTA on the very
// first screen, so it must read as an optional aside rather than a second
// decision competing with "send me a code".
.role-intent {
  margin-bottom: 20px;
}

.role-intent__label {
  display: block;
  margin-bottom: 8px;
}

.role-intent__options {
  display: flex;
  flex-wrap: wrap;
}

.role-intent__chip {
  background: white;
  border: 1px solid $c-border-grey;
  border-radius: 999px;
  color: $c-soft-black;
  cursor: pointer;
  font-size: 14px;
  margin: 0 8px 8px 0;
  min-height: 44px;
  padding: 8px 16px;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: $c-information-blue;
  }

  &:focus-visible {
    outline: 2px solid $c-information-blue;
    outline-offset: 2px;
  }

  // Selection is ALSO stated in words by `.role-intent__notice`, so this fill
  // is reinforcement rather than the only signal.
  &--active {
    background-color: $c-backdrop-clear;
    border-color: $c-information-blue;
    color: $c-information-blue;
    font-weight: 500;
  }
}

.role-intent__notice {
  color: $c-secondary-grey;
  font-size: 13px;
  margin: 0;
}

// --- Registration form -----------------------------------------------------
.uc-form-required {
  color: $c-secondary-grey;
  font-size: 12px;
  font-weight: 400;
  margin-left: 4px;
}

html.is-rtl .uc-form-required {
  margin-left: 0;
  margin-right: 4px;
}

// --- Role choice -----------------------------------------------------------
// Equal-weight choice cards. This screen has no separate primary CTA -- the
// choice IS the action -- so neither option is styled as one.
.role-choice-group {
  margin-top: 20px;
}

.role-choice {
  background: white;
  border: 1px solid $c-border-grey;
  border-radius: 12px;
  cursor: pointer;
  display: block;
  padding: 16px;
  text-align: start;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  width: 100%;

  & + & {
    margin-top: 12px;
  }

  &:hover {
    background-color: $c-backdrop-clear;
    border-color: $c-information-blue;
  }

  &:focus-visible {
    outline: 2px solid $c-information-blue;
    outline-offset: 2px;
  }
}

.role-choice__label {
  color: $c-soft-black;
  display: block;
  font-size: 16px;
  font-weight: 500;
}

.role-choice__hint {
  color: $c-secondary-grey;
  display: block;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 4px;
}

// --- OTP resend ------------------------------------------------------------
.otp-resend {
  margin-top: 12px;
  text-align: center;
}

.otp-resend__timer {
  color: $c-secondary-grey;
  // Tabular figures stop the row from reflowing as the countdown ticks.
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.otp-resend__notice {
  color: $c-information-blue;
  font-size: 13px;
  margin: 4px 0 0 0;
  text-align: center;
}

// The six boxes are laid out by _otpInput.scss; only the surrounding rhythm
// belongs to this card.
::v-deep .simple-otp-input {
  margin-top: 4px;
}

// 6 x 3rem boxes plus margins overflow a 375px card at the default size, so
// the boxes shrink rather than the row scrolling.
@include breakpoint-below('tiny') {
  ::v-deep .otp-single-input {
    font-size: 1.5rem;
    height: 2.75rem;
    width: 2.75rem;
  }
}

// --- Direction -------------------------------------------------------------
// `html.is-rtl * { direction: rtl }` in App/index.vue applies to every element,
// including phone fields, so a `dir` attribute or a bare `.ltr` class cannot
// win on specificity. Nesting under `html.is-rtl` is the pattern the signup
// forms already use for exactly this, and is what is reused here: the page
// stays RTL while digit runs read left-to-right.
html.is-rtl {
  .uc-link.subtext {
    margin: 2px 10px 0 0;
  }

  .phone-input,
  .phone-inline {
    direction: ltr;
  }

  // _otpInput.scss already sets direction: ltr on .simple-otp-input, but
  // `html.is-rtl *` outranks it on specificity, so it is restated here where
  // it can win.
  ::v-deep .simple-otp-input {
    direction: ltr;
  }

  // The number is shown back to the user as confirmation, so it stays on the
  // reading side of an RTL card rather than jumping to the far edge.
  p.phone-inline {
    text-align: right;
  }
}
</style>
